import { AssignStatusFilter } from "@/enum/list.enum";

// Canonical "Assigned" tab status set lives in ./assignedTabStatuses (single source of truth shared
// with the row/count/download paths). Re-exported here to keep the existing import path stable.
export { ASSIGNED_TAB_ASSIGN_STATUS_FILTERS } from "./assignedTabStatuses";

type ReviewScope = "selected" | "filtered" | "tab" | "all";

interface AssignedStatusFilterEntry {
	type?: string;
	value?: unknown;
}

const isAssignStatusFilter = (value: unknown): value is AssignStatusFilter => {
	return (
		typeof value === "string" &&
		Object.values(AssignStatusFilter).includes(value as AssignStatusFilter)
	);
};

const getFilterValueIds = (value: unknown): AssignStatusFilter[] => {
	if (!Array.isArray(value)) {
		return [];
	}

	return value
		.map((item) => {
			if (typeof item === "string") {
				return item;
			}

			if (item && typeof item === "object" && "id" in item) {
				return (item as { id?: unknown }).id;
			}

			return undefined;
		})
		.filter(isAssignStatusFilter);
};

export const getAssignedStatusFiltersFromSearchParams = (
	searchParams: URLSearchParams,
	baseAssignStatusFilters: AssignStatusFilter[]
): AssignStatusFilter[] | undefined => {
	const filterParam = searchParams.get("filter");

	if (!filterParam) {
		return undefined;
	}

	try {
		const parsedFilter = JSON.parse(filterParam) as Record<
			string,
			AssignedStatusFilterEntry | undefined
		>;
		const assignedStatusFilter = parsedFilter.assignedStatus;
		const selectedStatuses = getFilterValueIds(assignedStatusFilter?.value);

		if (selectedStatuses.length === 0) {
			return undefined;
		}

		if (assignedStatusFilter?.type === "dropdownExclude") {
			return baseAssignStatusFilters.filter((status) => !selectedStatuses.includes(status));
		}

		if (assignedStatusFilter?.type === "dropdown") {
			return selectedStatuses;
		}
	} catch {
		return undefined;
	}

	return undefined;
};

export const getAssignStatusFiltersForOpenInReview = ({
	scope,
	searchParams,
	tabAssignStatusFilters,
}: {
	scope: ReviewScope;
	searchParams: URLSearchParams;
	tabAssignStatusFilters: AssignStatusFilter[];
}): AssignStatusFilter[] | undefined => {
	if (scope === "filtered") {
		return (
			getAssignedStatusFiltersFromSearchParams(searchParams, tabAssignStatusFilters) ??
			tabAssignStatusFilters
		);
	}

	if (scope === "tab") {
		return tabAssignStatusFilters;
	}

	return undefined;
};
